import { Auth, CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import { AuthError, configureAuth, HomeboundUser, startLogin } from "@homebound/auth-components";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { amplifyConfig, getStage, useInternalPoolKey } from "src/context";
import { useViewport } from "src/contexts";
import { Button, Icon } from "~components";
import { Css } from "~generated/css";
import { isValidEmail } from "~utils";
import { AuthFormContainer } from "../components/AuthFormContainer";
import { AuthToggleLink } from "../components/AuthToggleLink";
import { EmailTextField } from "../components/EmailTextField";
import { GoogleAuthButton } from "../components/GoogleAuthButton";
import { GoogleAuthSection } from "../components/GoogleAuthSection";
import { LandingCtaWrapper } from "../components/LandingCtaWrapper";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { LocalProdWarning } from "./LocalProdWarning";

type SignInProps = {
  onLogin: (user: HomeboundUser) => Promise<void>;
  isInternal?: boolean;
};

export function SignIn(props: SignInProps) {
  const stage = getStage();
  const { isMobile } = useViewport();

  if (isMobile) {
    return <LoginForm {...props} />;
  }

  return (
    <div css={Css.df.fdc.mvh100.$}>
      {stage === "local-prod" && <LocalProdWarning />}
      <LoginForm {...props} />
    </div>
  );
}

function LoginLanding({ setStep }: { setStep: (step: "landing" | "email") => void }) {
  return (
    <LandingCtaWrapper title="Your new home starts here.">
      <div css={Css.df.fdc.gap2.ifMobile.wPx(200).$}>
        <GoogleAuthButton mode="signIn" onClick={() => void handleGoogleLogin(false)} />
        <Button variant="secondary" xss={Css.ba.bcGray800.plPx(4).ifMobile.w100.$} onClick={() => setStep("email")}>
          <span css={Css.sqPx(22).mr2.$}>
            <Icon name="email" />
          </span>
          Sign in with email
        </Button>
      </div>
      <AuthToggleLink mode="signIn" />
      <Button variant="link" onClick={() => void handleGoogleLogin(true)} xss={Css.ifMobile.mt2.$}>
        Internal User Login
      </Button>
    </LandingCtaWrapper>
  );
}

// LoginForm only handles external/homeowner users with magic links
// Internal users use the dedicated "Internal Login" button
function LoginForm(props: SignInProps) {
  const [email, setEmail] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const [step, setStep] = useState<"landing" | "email">(() => {
    return searchParams.get("signInError") !== null ? "email" : "landing";
  });

  // Handle initial error state on mount
  useEffect(() => {
    // Check sessionStorage for internal user wrong login flag
    const internalUserWrongLogin = sessionStorage.getItem("internalUserWrongLogin");

    if (internalUserWrongLogin) {
      sessionStorage.removeItem("internalUserWrongLogin");
      setStep("email");
      setError({
        code: "InternalUserWrongLogin",
        message: "We detected that you're an internal user. Please login via the 'Internal Users' button.",
      });
    } else if (searchParams.get("signInError") !== null) {
      // Check for signInError in query params (from OAuth redirect)
      // Message is empty because friendlyMessage() sets the user-facing error based on the code
      setError({ code: "UserNotFoundException", message: "" });
      // Clean up the URL by removing the error parameter
      navigate("/signIn", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The user manually entered their email, and must already be a user, trigger a magic link
  async function handleSignIn() {
    if (!isValidEmail(email)) {
      setError({ code: "ValidationError", message: "Please enter a valid email address" });
      return;
    }
    setError(null);
    setDisabled(true);
    // Send magic link code via email
    const { data, error } = await startLogin(email);
    setDisabled(false);
    setError(error || null);
    data && navigate("/enterCode", { state: { email } });
  }
  const isLoading = disabled || googleLoading;

  // Render the email form
  return step === "landing" ? (
    <LoginLanding setStep={setStep} />
  ) : // If we see `InternalUserWrongLogin` error, just show the error message
  // and internal login button
  error?.code === "InternalUserWrongLogin" ? (
    <div css={Css.df.fdc.jcc.aic.vh100.gap3.wPx(500).mxa.$}>
      <img src="/logo.svg" width="156" height="25" alt="Homebound" />
      <div css={Css.w100.tac.$}>
        {friendlyErrorMessage({ code: "InternalUserWrongLogin", message: "Internal User Wrong Login" })}
      </div>
      <Button
        variant="primary"
        onClick={() => {
          void handleGoogleLogin(true);
        }}
      >
        Internal User Login
      </Button>
    </div>
  ) : (
    <AuthFormContainer title="Sign In" onBack={() => setStep("landing")}>
      <div css={Css.df.fdc.aic.gap3.w100.ifMobile.gap2.h100.jcc.px4.$}>
        {!isMobile && <img src="/logo.svg" width="156" height="25" alt="Homebound" />}
        <EmailTextField
          value={email}
          onChange={(value) => {
            setEmail(value);
            setError(null);
          }}
          onEnter={() => !disabled && void handleSignIn()}
          disabled={isLoading}
          error={error ? friendlyErrorMessage(error) : null}
        />
        <div css={Css.ifDesktopAndUp.w100.$}>
          <Button variant="primary" disabled={isLoading || !email} onClick={handleSignIn}>
            Sign In
          </Button>
        </div>
        <GoogleAuthSection
          mode="signIn"
          onClick={() => {
            setGoogleLoading(true);
            void handleGoogleLogin(false);
          }}
        />
        <AuthToggleLink mode="signIn" />
      </div>
      {isLoading && <LoadingOverlay />}
    </AuthFormContainer>
  );
}

async function handleGoogleLogin(isInternal: boolean = false) {
  const stage = getStage();
  localStorage.setItem(useInternalPoolKey, isInternal ? "true" : "false");
  // Only reconfigure auth if switching to internal pool
  // (homeowner pool is already configured at app startup)
  if (isInternal) {
    // Configure auth - internal uses internal config
    configureAuth(amplifyConfig(stage, isInternal));
    // Wait for Amplify configuration to propagate internally before attempting login
    // configureAuth is synchronous but Amplify needs time to initialize with the new configuration
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });
  }
  // Trigger Google OAuth, i.e. redirect away & comes back to /auth/gcallback, routes to auth-components GoogleCallback
  await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google });
}

// SignIn-specific helpers
function friendlyErrorMessage(error: AuthError): string {
  switch (error.code) {
    case "InternalUserWrongLogin":
      return "We detected that you're an internal user. Please login via the 'Internal Users' button.";
    case "UserNotFoundException":
      return "This email isn't registered. Reach out to your Homebound contact if the problem persists.";
    case "ValidationError":
      return error.message || "Please enter a valid email address";
    default:
      return "Uh oh! Something went wrong";
  }
}
